import React, {useEffect, useState} from "react";
import {useIntl} from "react-intl";
import * as Yup from "yup";
import {FormikProps, useFormik} from "formik";
import md5 from "md5";
import {Auth, FormInput, notify} from "@housebets/housebets-x-package";

import {loginSuccess} from "app/store/slices/user";
import {useGTMEvents} from "app/hooks/useGTMEvents";
import apiAuth from "app/api/auth";

import {ISignInProps, TFormikConfig} from "./types";
import {AuthLinks} from "../AuthLinks";
import {emailSchema} from "app/utils/schemaValidation";
import { useDispatch } from "react-redux";

const formSchema = () =>
    Yup.object().shape({
        email: emailSchema,
        password: Yup.string().required("Field is required"),
        code: Yup.string(),
    });

const initForm = (formik: FormikProps<TFormikConfig>, setError?: (v: boolean) => void) => {
    formik.setValues({
        email: "",
        password: "",
        code: "",
    });
    formik.setErrors({});
    formik.setTouched({});
    setError?.(false);
};

export const SignIn: React.FC<ISignInProps> = ({onSubmit, onTabChange}) => {
    const dispatch = useDispatch();
    const {$t} = useIntl();
    const [, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const {form} = useGTMEvents();

    const formik: FormikProps<TFormikConfig> = useFormik<TFormikConfig>({
        initialValues: {
            email: "",
            password: "",
            code: "",
        },
        validationSchema: formSchema(),
        validateOnBlur: true,
        onSubmit: async (payload) => {
            const {email, password, code} = payload;
            try {
                let res;
                setIsLoading(true);
                if (code) {
                    res = await apiAuth.login("email", email, md5(password), +code);
                } else {
                    res = await apiAuth.login("email", email, md5(password));
                }
                const {status, data} = res;

                if (status === 200 && data.code === 0) {
                    dispatch(loginSuccess(data.data));
                    form.event({
                        event: "form_submit",
                        form_id: "signin_form",
                        form_name: "Sign In Form",
                    });
                    initForm(formik);
                    onSubmit();
                    return;
                }

                if (data.code === 13) {
                    setIsSignInFa2(true);
                    return;
                }

                if (data.code === 101) {
                    notify({
                        type: "error",
                        title: $t({id: "SystemError"}),
                    });
                    form.event({
                        event: "form_error",
                        form_id: "signin_form",
                        form_name: "Sign In Form",
                        additional_parameters: {
                            message: $t({id: "SystemError"}),
                        },
                    });
                    return;
                }
                notify({
                    type: "error",
                    title: data.code ? $t({id: `LOGIN_ERROR_${data.code}`}) : data.message,
                });
                form.event({
                    event: "form_error",
                    form_id: "signin_form",
                    form_name: "Sign In Form",
                    additional_parameters: {
                        message: data.code ? $t({id: `LOGIN_ERROR_${data.code}`}) : data.message,
                    },
                });
            } catch (er) {
                setError("some error");
            } finally {
                setIsLoading(false);
            }
        },
    });

    useEffect(() => {
        const keys = Object.keys(formik.errors);
        keys.forEach((item) => {
            form.error({
                field: item,
                message: formik.errors[item as keyof TFormikConfig] as string,
                form_id: "signin_form",
                form_name: "Sign In Form",
            });
        });
    }, [formik.errors]);

    useEffect(() => {
        initForm(formik);
    }, []);

    const [isSignInFa2, setIsSignInFa2] = useState(false);

    
    return (
        <Auth.Form.Wrapper>
            <form id="signin_form" autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                <Auth.Form.Row noMobile>
                    <Auth.Form.FormTitle>{$t({id: "LoginToAccount"})}</Auth.Form.FormTitle>
                </Auth.Form.Row>
                <Auth.Form.Row gap>
                    {isSignInFa2 ? (
                        <FormInput
                            placeholder={$t({id: "Enter2FaCode"})}
                            name="code"
                            type="text"
                            label={$t({id: "Code"})}
                            size="sm"
                            value={formik.values?.code}
                            errors={
                                (!!formik.errors?.code && !!formik.touched?.code) || error
                                    ? {
                                          message: formik.errors?.code || error,
                                      }
                                    : null
                            }
                            onChange={(val) => formik.setFieldValue("code", val)}
                            onFocus={() => formik.setFieldTouched("code", false)}
                            onBlur={() => formik.setFieldTouched("code", true)}
                        />
                    ) : (
                        <div>
                            <FormInput
                                placeholder={$t({id: "EnterEmail"})}
                                name="email"
                                type="email"
                                label={$t({id: "EmailAddress"})}
                                required
                                size="sm"
                                value={formik.values?.email}
                                errors={
                                    (!!formik.errors?.email && !!("email" in formik.touched)) || error
                                        ? {
                                              message: formik.errors?.email || error,
                                          }
                                        : null
                                }
                                onChange={(val) => formik.setFieldValue("email", val)}
                                onFocus={() => formik.setFieldTouched("email", false)}
                                onBlur={() => formik.setFieldTouched("email", true)}
                            />
                            <FormInput
                                placeholder={$t({id: "EnterPassword"})}
                                name="password"
                                type="password"
                                label={$t({id: "Password"})}
                                required
                                size="sm"
                                value={formik.values?.password}
                                errors={
                                    (!!formik.errors?.password && !!("password" in formik.touched)) || error
                                        ? {
                                              message: formik.errors?.password || error,
                                          }
                                        : null
                                }
                                onChange={(val) => formik.setFieldValue("password", val)}
                                onFocus={() => formik.setFieldTouched("password", false)}
                                onBlur={() => formik.setFieldTouched("password", true)}
                            />
                        </div>
                    )}
                </Auth.Form.Row>
                <Auth.Form.Row>
                    <Auth.Form.ForgotPassword onClick={() => onTabChange(2)} text={$t({id: "ForgotPassword"}) + "?"} />
                </Auth.Form.Row>
                <Auth.Form.Row>
                    <Auth.Form.Button marginTop type="submit" text={$t({id: "SignIn"})} />
                </Auth.Form.Row>
            </form>
            <Auth.Form.Divider padding marginTop text={$t({id: "OrContinueWith"})} />
            <AuthLinks />
            <Auth.Form.NavLink
                text={$t({id: "NotAMember"})}
                linkText={$t({id: "CreateAccount"})}
                onClick={() => {
                    onTabChange(1);
                }}
            />
        </Auth.Form.Wrapper>
    );
};

SignIn.displayName = "Auth.SignIn";
